/**
 * Єдина іконна система (Lucide). Семантичні назви → компонент Lucide, щоб
 * по всьому застосунку був один stroke/optical-стиль. Використовуй <Icon name="…" />.
 */
import {
  LayoutDashboard, Users, CreditCard, TrendingUp, Target, Zap, Tag, Settings,
  Search, Bell, Menu, Pencil, Trash2, Plus, Download, SlidersHorizontal, Funnel,
  ArrowLeftRight, ChevronsUpDown, ChevronUp, ChevronDown, ChevronRight, ChevronLeft, Check, X,
  List, LayoutGrid, GripVertical, EllipsisVertical, Copy, Star,
  Type, Pilcrow, Calendar, CalendarClock, Mail, Hash, Percent, DollarSign, Phone,
  Link, Flag, Globe, Tags, SquareCheckBig, SquareFunction, ChartColumn, Sigma, ArrowUpRight,
  Clock, RefreshCw, CircleCheck, CircleX, ExternalLink, Send, Receipt, Lock, Eye, EyeOff, Undo2,
  Table2, Webhook, ScrollText, GitCompare,
  Play, Heart, HeartOff, Wrench, Dot, BarChart3, LineChart, Folder, GitBranch, Save, Lightbulb, ArrowUp,
  Building2, ShoppingCart, FileText,
  Briefcase, Package, Bookmark, MapPin, MessageCircle, Truck, Gift, Database, Layers, Ticket,
  Rocket, Sparkles, Megaphone, Coffee, AlertTriangle, Columns3, Camera, PanelLeft, Inbox,
} from 'lucide-svelte';

export const ICONS = {
  // nav
  dashboard: LayoutDashboard,
  users: Users,
  payments: CreditCard,
  cohorts: TrendingUp,
  funnels: Target,
  automations: Zap,
  products: Tag,
  settings: Settings,
  // topbar / actions
  search: Search,
  bell: Bell,
  menu: Menu,
  edit: Pencil,
  delete: Trash2,
  add: Plus,
  download: Download,
  filter: Funnel,
  sliders: SlidersHorizontal,
  reorder: ArrowLeftRight,
  sort: ChevronsUpDown,
  sortUp: ChevronUp,
  sortDown: ChevronDown,
  chevronDown: ChevronDown,
  chevronRight: ChevronRight,
  chevronLeft: ChevronLeft,
  export: Download,
  check: Check,
  close: X,
  listView: List,
  boardView: LayoutGrid,
  grip: GripVertical,
  more: EllipsisVertical,
  copy: Copy,
  star: Star,
  refresh: RefreshCw,
  externalLink: ExternalLink,
  send: Send,
  receipt: Receipt,
  lock: Lock,
  eye: Eye,
  eyeOff: EyeOff,
  restore: Undo2,
  table: Table2,
  webhook: Webhook,
  audit: ScrollText,
  dedup: GitCompare,
  play: Play,
  heart: Heart,
  heartOff: HeartOff,
  wrench: Wrench,
  dot: Dot,
  chartBars: BarChart3,
  chartLine: LineChart,
  folder: Folder,
  branch: GitBranch,
  save: Save,
  tip: Lightbulb,
  arrowUp: ArrowUp,
  building: Building2,
  cart: ShoppingCart,
  note: FileText,
  briefcase: Briefcase,
  package: Package,
  bookmark: Bookmark,
  mapPin: MapPin,
  message: MessageCircle,
  truck: Truck,
  gift: Gift,
  database: Database,
  layers: Layers,
  ticket: Ticket,
  rocket: Rocket,
  sparkles: Sparkles,
  megaphone: Megaphone,
  coffee: Coffee,
  clock: Clock,
  warning: AlertTriangle,
  columns: Columns3,
  camera: Camera,
  panelLeft: PanelLeft,
  inbox: Inbox,
  // field types
  single_line: Type,
  multi_line: Pilcrow,
  dropdown: ChevronsUpDown,
  date: Calendar,
  datetime: CalendarClock,
  email: Mail,
  number: Hash,
  auto_number: Hash,
  percent: Percent,
  currency: DollarSign,
  phone: Phone,
  url: Link,
  country: Globe,
  tags: Tags,
  checkbox: SquareCheckBig,
  formula: SquareFunction,
  stat: ChartColumn,
  linked_record: ArrowUpRight,
  linked_summary: Sigma,
  flag: Flag,
  // job/status
  statusPending: Clock,
  statusProcessing: RefreshCw,
  statusDone: CircleCheck,
  statusFail: CircleX,
};

export type IconName = keyof typeof ICONS;

/** Legacy емодзі-іконки таблиць (вже збережені в БД) → семантичні Lucide-назви. */
const TABLE_EMOJI_TO_ICON: Record<string, IconName> = {
  '📁': 'folder', '📊': 'chartBars', '👤': 'users', '💳': 'payments',
  '🏢': 'building', '📞': 'phone', '📅': 'date', '🛒': 'cart',
  '📝': 'note', '✅': 'check', '🎯': 'funnels', '🏷️': 'tags', '🏷': 'tags',
};

/** Набір іконок для пікера при створенні/редагуванні таблиці. */
export const TABLE_ICON_CHOICES: IconName[] = [
  'folder', 'note', 'bookmark', 'tags', 'flag',
  'users', 'briefcase', 'building', 'message', 'email', 'phone', 'send', 'megaphone',
  'payments', 'cart', 'receipt', 'ticket', 'gift', 'truck', 'package',
  'chartBars', 'funnels', 'database', 'layers',
  'date', 'clock', 'star', 'heart', 'rocket', 'sparkles', 'coffee',
  'country', 'mapPin', 'automations', 'settings', 'check',
];

/** Нормалізує збережене значення `icon` таблиці (legacy-емодзі АБО Lucide-назва) у IconName. */
export function tableIconName(icon: string | null | undefined): IconName {
  if (!icon) return 'folder';
  if (icon in ICONS) return icon as IconName;
  return TABLE_EMOJI_TO_ICON[icon] ?? 'folder';
}
